import { useState } from 'react'
import { PageContainer, ProTable } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { Alert, App, Button, Empty, Grid } from 'antd'
import { LockKeyhole, Plus } from 'lucide-react'
import { useMemberCreatorOptions } from '@/features/access/index.ts'
import { usePageTableNavigation } from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getCreatedAtSearch, getDateRangeFormValue } from '@/utils/list-query.ts'
import { getRoleListColumns } from './role-list-columns.tsx'
import { roleListPersistence, roleListSearchSchema, type RoleListSearch } from './role-list-search.ts'
import type { RoleRow } from './types.ts'

type RoleListFormValues = Omit<Partial<RoleListSearch>, 'isEnabled'> & {
  isEnabled?: string
  createdAtRange?: ReturnType<typeof getDateRangeFormValue>
}

const route = getRouteApi('/_authenticated/system-management/roles/')

export function RoleList() {
  const screens = Grid.useBreakpoint()
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message, modal } = App.useApp()
  const { currentSession } = route.useRouteContext()
  const { search, navigateSearch } = usePersistedListQuery({
    persistence: roleListPersistence,
    raw: route.useSearch(),
    accountId: currentSession.id,
    navigate: route.useNavigate()
  })
  const [selected, setSelected] = useState<RoleRow[]>([])
  const roles = useQuery({ ...trpc.authorization.listRoles.queryOptions(search), placeholderData: keepPreviousData })
  const status = useMutation(trpc.authorization.batchSetEnabled.mutationOptions())
  const creators = useMemberCreatorOptions('role.read', search.creatorMemberId)
  const table = usePageTableNavigation<RoleListSearch, RoleListFormValues>({
    defaultSearch: roleListSearchSchema.parse({}),
    search,
    navigate: navigateSearch,
    getFormInitialValues: (query) => ({
      ...query,
      isEnabled: query.isEnabled?.toString(),
      createdAtRange: getDateRangeFormValue(query.createdFrom, query.createdTo)
    }),
    getSearchFromForm: (values) => ({
      ...values,
      isEnabled: values.isEnabled === undefined ? undefined : values.isEnabled === 'true',
      ...getCreatedAtSearch(values.createdAtRange ?? null)
    }),
    onQueryChange: () => setSelected([]),
    showTotal: (total) => `共 ${total} 个角色`
  })
  const refresh = async () => {
    await Promise.all([
      cache.invalidateQueries(trpc.authorization.listRoles.queryFilter()),
      cache.invalidateQueries(trpc.authorization.roleDetail.queryFilter()),
      cache.invalidateQueries(trpc.authorization.roleOptions.queryFilter()),
      cache.invalidateQueries(trpc.auth.session.queryFilter())
    ])
    setSelected([])
  }
  const setEnabled = (rows: RoleRow[], enabled: boolean) => {
    modal.confirm({
      title: enabled ? '启用所选角色？' : '停用所选角色？',
      content: `涉及 ${rows.length} 个角色、${rows.reduce((sum, role) => sum + role.accountCount, 0)} 条账号关联。授权会在后续请求生效；活跃账号必须保留有效角色。`,
      okText: enabled ? '启用' : '停用',
      cancelText: '取消',
      okButtonProps: { danger: !enabled },
      onOk: async () => {
        try {
          await status.mutateAsync({
            isEnabled: enabled,
            roles: rows.map((role) => ({ roleId: role.id, expectedVersion: role.version }))
          })
          await refresh()
          void message.success('角色状态已更新')
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '更新失败')
          throw error
        }
      }
    })
  }
  const columns = getRoleListColumns({
    search,
    creators,
    statusPending: status.isPending,
    fixActions: !!screens.lg,
    onSetEnabled: setEnabled
  })

  return (
    <PageContainer
      title="角色权限"
      content={
        <p className="text-content-muted mt-0 mb-0 text-sm">管理角色的访问范围与业务权限，账号可关联多个角色。</p>
      }
      breadcrumbRender={false}
    >
      {roles.isError && <Alert type="error" showIcon title={roles.error.message} className="mb-4" />}
      <ProTable<RoleRow, RoleListFormValues>
        headerTitle={
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold">角色列表</span>
            <span className="bg-surface-muted text-content-muted rounded-md px-2 py-0.5 text-xs font-normal tabular-nums">
              {roles.data?.total ?? 0}
            </span>
          </div>
        }
        scroll={{ x: '58rem' }}
        tableLayout="fixed"
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="没有找到符合条件的角色" /> }}
        rowKey="id"
        columns={columns}
        dataSource={roles.data?.data ?? []}
        loading={roles.isLoading || roles.isFetching}
        formRef={table.formRef}
        form={{ initialValues: table.formInitialValues }}
        onSubmit={table.onSubmit}
        onReset={table.onReset}
        pagination={{ ...table.pagination, total: roles.data?.total ?? 0 }}
        options={{ reload: () => roles.refetch() }}
        search={{
          labelWidth: 'auto',
          span: { xs: 24, sm: 12, md: 8, lg: 8, xl: 8, xxl: 8 },
          collapseRender: (collapsed) => <span className="whitespace-nowrap">{collapsed ? '展开' : '收起'}</span>
        }}
        columnsState={{ persistenceKey: 'super-mega:roles:columns', persistenceType: 'localStorage' }}
        rowSelection={{
          columnWidth: '2.5rem',
          selectedRowKeys: selected.map((row) => row.id),
          onChange: (_, rows) => setSelected(rows),
          getCheckboxProps: (row) => ({
            disabled: row.isProtected || status.isPending,
            'aria-label': `选择角色 ${row.name}`
          })
        }}
        tableAlertRender={() => `已选 ${selected.length} 个角色`}
        tableAlertOptionRender={() => (
          <div className="flex gap-2">
            <Button onClick={() => setEnabled(selected, true)} disabled={!selected.length || status.isPending}>
              启用
            </Button>
            <Button danger onClick={() => setEnabled(selected, false)} disabled={!selected.length || status.isPending}>
              停用
            </Button>
          </div>
        )}
        // Ant Design Table 的原生回调包含分页、筛选、排序和操作类型四个参数。
        // eslint-disable-next-line max-params
        onChange={(_, __, sorter, extra) => {
          if (extra.action !== 'sort') return
          setSelected([])
          const current = Array.isArray(sorter) ? sorter[0] : sorter
          void navigateSearch({
            search: (previous) => ({
              ...previous,
              current: 1,
              sortField: current.field === 'name' ? 'name' : 'createdAt',
              sortOrder: current.order === 'ascend' ? 'asc' : 'desc'
            })
          })
        }}
        toolBarRender={() => [
          <Link key="new" to="/system-management/roles/new">
            <Button type="primary" icon={<Plus aria-hidden size={16} />}>
              新增角色
            </Button>
          </Link>
        ]}
      />
      <p className="text-content-soft mt-3 flex items-start gap-1.5 text-xs leading-5">
        <LockKeyhole className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        系统内置角色仅支持查看；同一账号关联多个角色时，权限合并生效。
      </p>
    </PageContainer>
  )
}
